import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { GolfEvent } from '../../../models/GolfEvent';
import { GolfEventField } from '../../../models/golfeventfield';
import { Team } from '../../../models/team';
import { NgFor, NgIf } from '@angular/common';
import { SelectableObject } from '../../../models/viewmodels/selectableobject';
import { FormsModule } from '@angular/forms';
import { CreateTeamRequest } from '../../../models/createTeamRequest';
import { UpdateTeamRequest } from '../../../models/updateTeamRequest';
import { AuthenticationService } from '../../../services/authentication.service';
import { GolfEventService } from '../../../services/golf-event.service';
import { ApplicationService } from '../../../services/application.service';

@Component({
  selector: 'app-create-team',
  standalone: true,
  imports: [NgFor, NgIf, FormsModule],
  templateUrl: './create-team.component.html',
  styleUrl: './create-team.component.css'
})
export class CreateTeamComponent implements OnChanges {
  constructor(private authenticationService:AuthenticationService, private golfEventService:GolfEventService, private applicationService:ApplicationService) {}

  @Input() league:GolfEvent|null = null;
  @Input() paidPlayers:Array<GolfEventField> = [];
  @Input() teamToEdit:Team|null = null;

  @Output() saved = new EventEmitter<void>;
  @Output() canceled = new EventEmitter<void>;

  selectablePlayers:Array<SelectableObject> = [];
  selectedPlayers:Array<SelectableObject> = [];
  teamName:string = '';
  private initializedForTeamId:number|null = null;

  get isEditing():boolean {
    return !!this.teamToEdit;
  }

  get formTitle():string {
    return this.isEditing ? 'Edit Team' : 'Create Team';
  }

  get saveLabel():string {
    return this.isEditing ? 'Save Team' : 'Save Team';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const teamChanged = changes['teamToEdit']
      && this.teamToEdit?.id !== this.initializedForTeamId;
    const playersFirstLoad = changes['paidPlayers']
      && this.selectablePlayers.length === 0
      && this.paidPlayers.length > 0;

    if (teamChanged || playersFirstLoad || (changes['teamToEdit'] && !this.teamToEdit && changes['teamToEdit'].previousValue)) {
      this.buildSelectablePlayers();
    }
  }

  buildSelectablePlayers():void {
    this.selectablePlayers = this.paidPlayers.map(p => ({ isSelected: false, obj: p }));

    if (this.teamToEdit) {
      const selectedIds = new Set(this.teamToEdit.players.map(p => Number(p.id)));
      this.selectablePlayers.forEach(player => {
        player.isSelected = selectedIds.has(Number(player.obj.playerId));
      });
      this.selectedPlayers = this.selectablePlayers.filter(s => s.isSelected);
      this.teamName = this.teamToEdit.teamName;
      this.initializedForTeamId = this.teamToEdit.id;
    } else {
      this.selectedPlayers = [];
      this.teamName = '';
      this.initializedForTeamId = null;
    }
  }

  addPlayerToTeam(player:SelectableObject) {
    player.isSelected = true;
    this.selectedPlayers = this.selectablePlayers.filter(s => s.isSelected);
    if (!this.isEditing && !this.teamName) {
      this.teamName = this.buildDefaultTeamName();
    }
  }

  removePlayerFromTeam(player:SelectableObject) {
    player.isSelected = false;
    this.selectedPlayers = this.selectablePlayers.filter(s => s.isSelected);
  }

  buildDefaultTeamName():string {
    return this.selectedPlayers.map(p => p.obj.lastName).join('/');
  }

  async saveTeam() {
    if(!this.league) return;
    if(!this.authenticationService.loginResult || !this.authenticationService.loginResult.player) return;
    if(!this.teamName?.trim()) return;
    if(this.selectedPlayers.length === 0) return;

    const playerId = this.authenticationService.loginResult.player.playerId;
    this.applicationService.showGlobalSpinner();

    if (this.teamToEdit) {
      const request:UpdateTeamRequest = {
        teamId: this.teamToEdit.id,
        teamName: this.teamName.trim(),
        playerIds: this.selectedPlayers.map(sp => sp.obj.playerId),
        updatedByPlayerId: playerId
      };
      await this.golfEventService.updateTeam(request);
    } else {
      const request:CreateTeamRequest = {
        createdByPlayerId: playerId,
        golfEventId: this.league.id,
        teamName: this.teamName.trim(),
        playerIds: this.selectedPlayers.map(sp => sp.obj.playerId)
      };
      await this.golfEventService.createTeam(request);
    }

    this.applicationService.hideGlobalSpinner();
    this.saved.emit();
  }

  async cancel() {
    this.canceled.emit();
  }
}
